import type { ComponentProps, ReactElement } from "react";
import { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
    SearchResultsTable,
    StyledScrollableTableContent,
} from "app/pages/sales-intelligence/common-components/search-results-table";
import type { AppsSearchTableData } from "app/pages/sales-intelligence/modules/apps-search/types/table";
import {
    findAppsUpdateSearchParams,
    setSelectedRows,
} from "app/pages/sales-intelligence/store/find-apps/action-creators";
import {
    selectActiveAppSourceType,
    selectAppsSearchRequestDto,
    selectIsRowSelectionEnabled,
    selectSelectedRowKeys,
} from "app/pages/sales-intelligence/store/find-apps/selectors";
import type { IFindAppsCommonRowDto } from "app/pages/sales-intelligence/store/find-apps/types/dto";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import FindAppsTableActionsContainer from "./FindAppsTableActionsContainer";
import SearchRowContainer from "./SearchRowContainer";

const DEFAULT_TABLE_DATA = { columns: [], rows: [], totalCount: 0 };

type TableProps<R extends object> = ComponentProps<typeof SearchResultsTable<R>>;

type Props<R extends IPlainObject> = {
    isLoading: boolean;
    data?: AppsSearchTableData<R>;
};

const FindAppsTableContainer = <R extends IFindAppsCommonRowDto>({
    isLoading,
    data = DEFAULT_TABLE_DATA,
}: Props<R>): ReactElement => {
    const dispatch = useDispatch();

    const activeAppSourceType = useSelector(selectActiveAppSourceType);
    const isRowSelectionEnabled = useSelector(selectIsRowSelectionEnabled);
    const selectedRowKeys = useSelector(selectSelectedRowKeys);
    const searchRequestParams = useSelector(selectAppsSearchRequestDto);

    const handleSortChange: TableProps<R>["onSort"] = ({ order, columnKey }): void => {
        dispatch(
            findAppsUpdateSearchParams([
                activeAppSourceType,
                { orderBy: columnKey as string, asc: order === "ascend" },
            ]),
        );
    };

    useEffect(() => {
        dispatch(setSelectedRows([]));
    }, [searchRequestParams.filters]);

    const selectionProps: TableProps<R>["selection"] = useMemo(
        () => ({
            enabled: isRowSelectionEnabled,
            selectedRowKeys,
            onChange(_keys, rows, _info) {
                dispatch(setSelectedRows(rows));
            },
        }),
        [isRowSelectionEnabled, selectedRowKeys],
    );

    const paginationProps: TableProps<R>["pagination"] = useMemo(
        () => ({
            pageSize: searchRequestParams.pageSize,
            total: data.totalCount,
            current: searchRequestParams.page,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                dispatch(findAppsUpdateSearchParams([activeAppSourceType, { page: nextPage }]));
            },
        }),
        [searchRequestParams.pageSize, searchRequestParams.page, data.totalCount, activeAppSourceType],
    );

    return (
        <>
            <FindAppsTableActionsContainer
                areTableActionsDisabled={isLoading}
                numberOfResults={data.totalCount}
            />
            <SearchRowContainer />
            <StyledScrollableTableContent>
                <SearchResultsTable<R>
                    isLoading={isLoading}
                    data={data.rows as R[]}
                    columnConfig={data.columns}
                    selection={selectionProps}
                    pagination={paginationProps}
                    onSort={handleSortChange}
                />
            </StyledScrollableTableContent>
        </>
    );
};

export default FindAppsTableContainer;
